import { useDrawer } from '@app/src/components/Drawer/DrawerContext'
import DrawerViewCompany from '@app/src/components/Drawer/Views/DrawerViewCompany'
import { AvatarSize } from '@app/src/components/Ui/Avatar'
import useIsTextTruncated from '@app/src/hooks/useIsTextTruncated'
import CompanyAvatar from '@app/src/pages/ResourceCollection/Collections/CellComponents/CompanyAvatar'
import type { GenericOrganization } from '@app/src/types/organizations'
import { resolveCompanyLogoImageUrl } from '@app/src/utils/resolveCompanyLogoImageUrl'
import paths from '@app/src/wf-constants/paths'
import { DeleteOutlined } from '@mui/icons-material'
import type { TypographyProps } from '@mui/material'
import { ButtonBase, Stack, Tooltip, Typography } from '@mui/material'
import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { generatePath, useHistory } from 'react-router'
import type { TextTruncateProps } from 'react-text-truncate'
import TextTruncate from 'react-text-truncate'
import type { ParentCellOrFragmentProps } from './ParentCellOrFragment'
import ParentCellOrFragment from './ParentCellOrFragment'

export type CompanyCellProps = {
  company?: GenericOrganization
  drawer?: boolean
  maxRows?: TextTruncateProps['line']
  disableCell?: boolean
  disableTooltip?: boolean
  disableTruncate?: boolean
  disableAvatar?: boolean
  sx?: ParentCellOrFragmentProps['sx']
  isPartialProviderData?: boolean
} & TypographyProps

export enum DrawerViews {
  Company = 'Company',
  RiskCard = 'RiskCard',
  FinalRiskRating = 'FinalRiskRating',
  DeleteProvider = 'DeleteProvider',
  LinkProvider = 'LinkProvider',
}

export const getOrganizationDisplayName = (company: GenericOrganization) => company?.organization?.name ?? company.name

const CompanyCell: React.FC<CompanyCellProps> = ({
  company,
  drawer,
  maxRows = 1,
  disableCell,
  disableTooltip,
  disableTruncate,
  disableAvatar,
  sx,
  isPartialProviderData,
  ...props
}) => {
  const { openDrawer } = useDrawer()
  const { formatMessage } = useIntl()
  const history = useHistory()
  const [truncated, setTruncated] = useState(false)
  // Only measure on the single-line (disableTruncate) path; the TextTruncate path drives the tooltip
  // via onTruncated, so measuring there would add a ResizeObserver per row for nothing.
  const { ref: nameRef, isTruncated } = useIsTextTruncated<HTMLSpanElement>(
    company ? getOrganizationDisplayName(company) : undefined,
    disableTruncate,
  )
  const showTooltip = disableTruncate ? isTruncated : truncated

  if (!company) {
    return (
      <ParentCellOrFragment disableCell={disableCell} sx={sx}>
        <Typography variant="body1">-</Typography>
      </ParentCellOrFragment>
    )
  }

  const companyName = getOrganizationDisplayName(company)
  const logoImageUrl = resolveCompanyLogoImageUrl(company)

  if (company.deletedAt)
    return (
      <ParentCellOrFragment disableCell={disableCell} sx={sx}>
        <Tooltip title={formatMessage({ id: 'general.deletedCompanyTooltip' })} arrow placement="top">
          <Stack direction="row" alignItems="center" spacing={1}>
            <DeleteOutlined color="disabled" />
            <Stack>
              <Typography variant="body1" color="action.disabled" component="span" {...props}>
                {disableTruncate ? companyName : <TextTruncate line={maxRows} text={companyName} />}
              </Typography>
            </Stack>
          </Stack>
        </Tooltip>
      </ParentCellOrFragment>
    )

  return (
    <ParentCellOrFragment disableCell={disableCell} sx={sx}>
      <Tooltip title={showTooltip ? companyName : undefined} disableHoverListener={disableTooltip || !showTooltip}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          component={drawer ? ButtonBase : 'span'}
          onClick={
            drawer
              ? (e: React.MouseEvent) => {
                  if (e.metaKey || e.ctrlKey) {
                    history.push(
                      generatePath(paths.resourceExplorer, {
                        resourceType: company.type.toLowerCase(),
                        resourceId: company.id,
                      }),
                    )
                  } else {
                    openDrawer(<DrawerViewCompany isPartialProviderData={isPartialProviderData} company={company} />, {
                      showAboveDialogs: true,
                    })
                  }
                }
              : undefined
          }
          width="100%"
          data-testid="open-company-drawer"
        >
          {!disableAvatar && (
            <CompanyAvatar
              organizationName={companyName}
              alt={companyName}
              imageUrl={logoImageUrl}
              size={AvatarSize.Small}
              unlinked={!company.linked}
            />
          )}
          <Typography
            ref={nameRef}
            variant="body1"
            align="left"
            sx={{ '&:hover': { textDecoration: drawer ? 'underline' : 'none' } }}
            flexGrow={1}
            minWidth={0}
            component="span"
            data-testid="company-cell-name"
            {...props}
          >
            {disableTruncate ? (
              companyName
            ) : (
              <TextTruncate line={maxRows} text={companyName} onTruncated={() => setTruncated(true)} />
            )}
          </Typography>
        </Stack>
      </Tooltip>
    </ParentCellOrFragment>
  )
}

export default CompanyCell
